import React from 'react';

import { GlyphName } from '../icon/Icon';

import { EmblemEndDetailWrapper } from './EmblemEndDetailWrapper';
import { EmblemIcon } from './EmblemIcon';
import { EmblemProvider } from './EmblemProvider';
import { EmblemText } from './EmblemText';
import { EmblemWrapper } from './EmblemWrapper';
import { EmblemColor, EmblemSize, EmblemVariant } from './types';

export type EmblemProps = {
  color?: EmblemColor; // only hash colors are supported
  size?: EmblemSize;
  variant?: EmblemVariant;
  text: React.ReactNode;
  width?: number | 'auto';
  startDetail?: GlyphName | React.ReactNode;
  onEndDetailClick?: React.MouseEventHandler<HTMLElement>;
  endDetail?: GlyphName | React.ReactNode;
  className?: string;
  active?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onDoubleClick?: React.MouseEventHandler<HTMLElement>;
  disableNativeTooltip?: boolean;
  role?: React.AriaRole;
  'aria-label'?: string;
  'data-role'?: string;
  'data-id'?: string;
};

export const Emblem: React.FC<EmblemProps> = ({
  color,
  size = 'sm',
  variant = 'light',
  active = false,
  text,
  width,
  startDetail,
  onEndDetailClick,
  endDetail,
  className,
  onClick,
  onDoubleClick,
  disableNativeTooltip,
  role,
  'aria-label': ariaLabel,
  'data-role': dataRole = 'emblem',
  'data-id': dataId,
}) => {
  return (
    <EmblemProvider emblemActive={active} variant={variant} size={size}>
      <EmblemWrapper
        color={color}
        width={width}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        className={className}
        role={role}
        aria-label={ariaLabel}
        data-role={dataRole}
        data-id={dataId}
      >
        {startDetail && <Detail value={startDetail} />}

        <EmblemText disableNativeTooltip={disableNativeTooltip}>{text}</EmblemText>

        {endDetail && (
          <EmblemEndDetailWrapper onClick={onEndDetailClick}>
            {endDetail && <Detail value={endDetail} />}
          </EmblemEndDetailWrapper>
        )}
      </EmblemWrapper>
    </EmblemProvider>
  );
};

function isGlyphName(value: GlyphName | React.ReactNode): value is GlyphName {
  return typeof value === 'string';
}

function Detail({ value }: { value: GlyphName | React.ReactNode }) {
  if (isGlyphName(value)) {
    return <EmblemIcon glyph={value} />;
  }

  return <>{value}</>;
}
